import { router } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'

import { titleize } from '../../../utils/string'

function FlashAlert({ variant, message, onDismiss, textDark }) {
  if (!message) return null

  const className = [
    'alert',
    `alert-${variant}`,
    textDark ? 'text-dark' : '',
    'alert-dismissible',
    'fade',
    'show',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} role="alert">
      <h4 className="alert-heading mb-0">{message}</h4>
      <button
        type="button"
        className="btn-close"
        data-bs-dismiss="alert"
        aria-label="Close"
        onClick={onDismiss}
      ></button>
    </div>
  )
}

function getDisplayName(employee) {
  return employee?.full_name || employee?.email || ''
}

function formatBalance(value) {
  if (value == null) return '0'
  return Number(value).toFixed(2).replace(/\.00$/, '')
}

export default function Index({ employees, refills, notice, alert }) {
  const [selected, setSelected] = useState([])
  const [form, setForm] = useState({
    refill_sick: false,
    refill_vacation: false,
    sick_amount: '',
    vacation_amount: '',
    effective_on: '',
    message: '',
  })
  const [flashNotice, setFlashNotice] = useState(notice)
  const [flashAlert, setFlashAlert] = useState(alert)

  useEffect(() => {
    if (notice) setFlashNotice(notice)
    if (alert) setFlashAlert(alert)
  }, [notice, alert])

  const allSelected = useMemo(() => {
    if (!employees?.length) return false
    return selected.length === employees.length
  }, [employees, selected])

  const toggleAll = () => {
    if (allSelected) {
      setSelected([])
    } else {
      setSelected(employees.map((e) => e.id))
    }
  }

  const toggleEmployee = (id) => {
    setSelected((list) => {
      if (list.includes(id)) return list.filter((v) => v !== id)
      return [...list, id]
    })
  }

  const submit = (e) => {
    e.preventDefault()
    router.post('/company_settings/leave_refills', {
      leave_refill: {
        user_ids: selected,
        sick_amount: form.refill_sick ? form.sick_amount : null,
        vacation_amount: form.refill_vacation ? form.vacation_amount : null,
        effective_on: form.effective_on || null,
        message: form.message || null,
      },
    })
  }

  return (
    <div className="container-fluid">
      <div className="header">
        <div className="header-body">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="header-pretitle">Human Resources</h6>
              <h1 className="header-title text-truncate">Leave Credit Refills</h1>
            </div>
          </div>
        </div>
      </div>

      <FlashAlert variant="danger" message={flashAlert} onDismiss={() => setFlashAlert(null)} />
      <FlashAlert variant="success" message={flashNotice} textDark onDismiss={() => setFlashNotice(null)} />

      <div className="card">
        <div className="card-header">
          <h4 className="card-header-title">Refill Credits</h4>
        </div>
        <div className="card-body">
          <form onSubmit={submit}>
            <div className="row">
              <div className="col-12 col-md-4">
                <div className="form-group">
                  <div className="form-check mb-2">
                    <input
                      id="refill-sick"
                      type="checkbox"
                      className="form-check-input"
                      checked={form.refill_sick}
                      onChange={(e) => setForm((f) => ({ ...f, refill_sick: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="refill-sick">
                      Refill Sick Leave
                    </label>
                  </div>
                  <label className="form-label">Sick Leave (days)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="form-control"
                    disabled={!form.refill_sick}
                    value={form.sick_amount}
                    onChange={(e) => setForm((f) => ({ ...f, sick_amount: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="form-group">
                  <div className="form-check mb-2">
                    <input
                      id="refill-vacation"
                      type="checkbox"
                      className="form-check-input"
                      checked={form.refill_vacation}
                      onChange={(e) => setForm((f) => ({ ...f, refill_vacation: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="refill-vacation">
                      Refill Vacation Leave
                    </label>
                  </div>
                  <label className="form-label">Vacation Leave (days)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    className="form-control"
                    disabled={!form.refill_vacation}
                    value={form.vacation_amount}
                    onChange={(e) => setForm((f) => ({ ...f, vacation_amount: e.target.value }))}
                  />
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="form-group">
                  <label className="form-label">Effective On</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.effective_on}
                    onChange={(e) => setForm((f) => ({ ...f, effective_on: e.target.value }))}
                  />
                  <small className="text-body-secondary">Leave blank to use today.</small>
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">Message (optional)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="table-responsive mt-4">
              <table className="table table-sm table-hover table-nowrap">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input type="checkbox" className="form-check-input" checked={allSelected} onChange={toggleAll} />
                    </th>
                    <th>Employee</th>
                    <th className="text-end">Sick Leave</th>
                    <th className="text-end">Vacation Leave</th>
                  </tr>
                </thead>
                <tbody>
                  {(employees || []).map((employee) => (
                    <tr key={employee.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selected.includes(employee.id)}
                          onChange={() => toggleEmployee(employee.id)}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="avatar avatar-sm me-2">
                            <img
                              src={employee.avatar_url}
                              className="avatar-img rounded-circle"
                              alt={employee.full_name || employee.email}
                            />
                          </div>
                          <div>
                            <div className="fw-semibold">{getDisplayName(employee)}</div>
                            <div className="small text-body-secondary">{employee.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-end">{formatBalance(employee.sick_leave_balance)}</td>
                      <td className="text-end">{formatBalance(employee.vacation_leave_balance)}</td>
                    </tr>
                  ))}

                  {!employees?.length ? (
                    <tr>
                      <td colSpan={4} className="text-center text-body-secondary py-4">
                        No employees found.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <button type="submit" className="btn btn-primary" disabled={!selected.length}>
              Refill Selected Employees
            </button>
          </form>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header">
          <h4 className="card-header-title">Refill History</h4>
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-hover table-nowrap card-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Type</th>
                <th className="text-end">Amount</th>
                <th>Effective On</th>
                <th>Initiated By</th>
                <th>Created At</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {(refills || []).map((refill) => (
                <tr key={refill.id}>
                  <td>{getDisplayName(refill.employee)}</td>
                  <td>{titleize(refill.leave_type)}</td>
                  <td className="text-end">{formatBalance(refill.amount)}</td>
                  <td>{refill.effective_on_label}</td>
                  <td>{getDisplayName(refill.created_by)}</td>
                  <td>{refill.created_at_label}</td>
                  <td>{refill.message || '—'}</td>
                </tr>
              ))}

              {!refills?.length ? (
                <tr>
                  <td colSpan={7} className="text-center text-body-secondary py-4">
                    No refills recorded yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
